import { useCallback, useMemo, useState } from 'react'
import Header from './components/Header'
import StatsBar from './components/StatsBar'
import FilterPanel from './components/FilterPanel'
import EventList from './components/EventList'
import EventDetail from './components/EventDetail'
import FireMap from './components/FireMap'
import Loader from './components/Loader'
import ErrorState from './components/ErrorState'
import { useWildfires } from './hooks/useWildfires'
import { REGION_OPTIONS, summarize } from './utils/events'

const DEFAULT_DAYS = 7

function App() {
    const [days, setDays] = useState(DEFAULT_DAYS)
    const [query, setQuery] = useState('')
    const [activeRegions, setActiveRegions] = useState([])
    const [activeSources, setActiveSources] = useState([])
    const [sort, setSort] = useState('recent')
    const [selectedId, setSelectedId] = useState(null)
    const [sidebarOpen, setSidebarOpen] = useState(true)

    const { status, events, error, fetchedAt, reload } = useWildfires(days)

    // Compteurs calculés sur le jeu complet : les pastilles gardent le même
    // libellé quand on coche/décoche un filtre.
    const regionFacets = useMemo(() => {
        const counts = new Map()
        for (const ev of events) counts.set(ev.regionId, (counts.get(ev.regionId) ?? 0) + 1)
        return REGION_OPTIONS
            .filter(r => counts.has(r.id))
            .map(r => ({ ...r, count: counts.get(r.id) }))
            .sort((a, b) => b.count - a.count)
    }, [events])

    const sourceFacets = useMemo(() => {
        const counts = new Map()
        for (const ev of events) counts.set(ev.sourceId, (counts.get(ev.sourceId) ?? 0) + 1)
        return [...counts.entries()]
            .map(([id, count]) => ({ id, count }))
            .sort((a, b) => b.count - a.count)
    }, [events])

    const visibleEvents = useMemo(() => {
        const needle = query.trim().toLowerCase()
        const filtered = events.filter(ev => {
            if (activeRegions.length && !activeRegions.includes(ev.regionId)) return false
            if (activeSources.length && !activeSources.includes(ev.sourceId)) return false
            if (needle && !`${ev.title} ${ev.description ?? ''} ${ev.id}`.toLowerCase().includes(needle)) return false
            return true
        })

        return sort === 'area'
            ? [...filtered].sort((a, b) => (b.hectares ?? -1) - (a.hectares ?? -1))
            : filtered
    }, [events, activeRegions, activeSources, query, sort])

    const stats = useMemo(() => summarize(visibleEvents), [visibleEvents])
    const selectedEvent = useMemo(
        () => visibleEvents.find(ev => ev.id === selectedId) ?? null,
        [visibleEvents, selectedId],
    )

    const toggle = setter => value =>
        setter(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]))

    const isFiltered = Boolean(query || activeRegions.length || activeSources.length || sort !== 'recent')

    const resetFilters = () => {
        setQuery('')
        setActiveRegions([])
        setActiveSources([])
        setSort('recent')
    }

    // Référence stable : MarkerCluster reconstruit son calque à chaque changement.
    const handleSelect = useCallback(id => {
        setSelectedId(id)
        // Sur mobile le panneau latéral recouvre la carte : le replier pour
        // laisser voir le feu sélectionné et son détail.
        if (window.matchMedia('(max-width: 900px)').matches) setSidebarOpen(false)
    }, [])

    const isFirstLoad = status === 'loading' && events.length === 0

    return (
        <div className="app">
            <Header
                status={status}
                fetchedAt={fetchedAt}
                onReload={reload}
                sidebarOpen={sidebarOpen}
                onToggleSidebar={() => setSidebarOpen(open => !open)}
            />

            <div className="app__body">
                <aside className={`sidebar${sidebarOpen ? '' : ' is-collapsed'}`}>
                    <FilterPanel
                        days={days}
                        onDaysChange={setDays}
                        query={query}
                        onQueryChange={setQuery}
                        regions={regionFacets}
                        activeRegions={activeRegions}
                        onToggleRegion={toggle(setActiveRegions)}
                        sources={sourceFacets}
                        activeSources={activeSources}
                        onToggleSource={toggle(setActiveSources)}
                        sort={sort}
                        onSortChange={setSort}
                        onReset={resetFilters}
                        isFiltered={isFiltered}
                    />

                    <div className="sidebar__list">
                        {isFirstLoad
                            ? <Loader />
                            : <EventList events={visibleEvents} selectedId={selectedId} onSelect={handleSelect} />}
                    </div>
                </aside>

                <main className="workspace">
                    {status === 'error' ? (
                        <ErrorState message={error} onRetry={reload} />
                    ) : (
                        <>
                            <StatsBar stats={stats} totalCount={events.length} />
                            <FireMap
                                events={visibleEvents}
                                selectedEvent={selectedEvent}
                                onSelect={handleSelect}
                            />
                            {selectedEvent && (
                                <EventDetail event={selectedEvent} onClose={() => setSelectedId(null)} />
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    )
}

export default App
